/* eslint-disable @typescript-eslint/naming-convention */
import { action, computed, IReactionDisposer, makeObservable, observable, runInAction } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { injectable } from '@servicetitan/react-ioc';

import { ChatStore } from '../../common/stores/chat.store';
import {
    EquipmentsApi,
    InstalledEquipmentApiModel,
    MappedEquipmentApiModel,
} from '../../common/api/equipments.api';
import { MessageType } from '../../common/models/message-type.model';
import { ErrorModel } from '../../common/models/error.model';
import { ManualEquipmentData } from './equipment-manual-select.store';
import { getBaseRequestParams } from '../../common/utils/base-request-params';
import { MainStore } from '../../common/stores/main.store';
import { getEquipmentLabel } from '../components/search-equipment';

@injectable()
export class EquipmentPickerStore {
    @observable
    isFetchingInfo = false;

    @observable
    equipments: InstalledEquipmentApiModel[] = [];

    @observable
    loading = false;

    private loadEquipmentCancelToken: CancelTokenSource | undefined = undefined;

    private disposers: IReactionDisposer[] = [];

    constructor(
        private readonly equipmentsApi: EquipmentsApi,
        private readonly chatStore: ChatStore,
        private readonly mainStore: MainStore
    ) {
        makeObservable(this);
    }

    @computed
    get selectedEquipment() {
        if (!this.mainStore.selectedMonolithEquipment) {
            return undefined;
        }
        return {
            ...this.mainStore.selectedMonolithEquipment,
            label: getEquipmentLabel(this.mainStore.selectedMonolithEquipment),
        };
    }

    @action
    proceedFrom = async () => {
        await this.mainStore.waitForInit;

        if (this.mainStore.selectedTopic?.enabled) {
            this.loadEquipments();
            this.chatStore.resetMessaging();
            this.loadInitialMessages();
            return true;
        }

        return false;
    };

    @action
    dispose = () => {
        this.chatStore.resetMessaging();
        this.loadEquipmentCancelToken?.cancel();
        this.disposers.forEach(disposer => disposer());
    };

    loadInitialMessages = () => {
        this.chatStore.addMessage({
            isMine: false,
            type: MessageType.Text,
            text: `Use one of the 2 options below to select equipment`,
        });
    };

    @action
    loadEquipments = async () => {
        if (!this.mainStore.selectedAppointment) {
            return;
        }
        this.loading = true;
        this.loadEquipmentCancelToken?.cancel();
        try {
            this.loadEquipmentCancelToken = axios.CancelToken.source();
            const response = await this.equipmentsApi.getLocationEquipments(
                {
                    location_id: this.mainStore.selectedAppointment!.locationId,
                    // location_id: 7560388,
                    ...getBaseRequestParams(),
                },
                this.loadEquipmentCancelToken.token
            );
            this.loadEquipmentCancelToken = undefined;

            runInAction(() => {
                this.equipments = response.data.equipments.map(x => ({
                    ...x,
                    label: x.name ?? '',
                    disabled: false,
                }));
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    data: {
                        title: 'Something went wrong',
                        message: 'Unable to load the equipment list. Please try again later.',
                        link: {
                            text: 'Proceed without equipment',
                            to: `/${this.mainStore.selectedAppointment!.id}/${this.mainStore.selectedTopic!.id}/questions`,
                        },
                    } as ErrorModel,
                    type: MessageType.Error,
                },
            ]);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    showSelectEquipment = () => {
        this.chatStore.addMessage({
            isMine: false,
            type: MessageType.SearchEquipment,
        });
        this.chatStore.addMessage({
            type: MessageType.ManualEquipment,
            isMine: false,
            date: new Date(),
            showBubble: true,
        });
    };

    @action
    setSelectedEquipment = async (
        equipment: InstalledEquipmentApiModel
    ): Promise<MappedEquipmentApiModel[] | undefined> => {
        this.mainStore.setSelectedMonolithInstalledEquipment(equipment);

        // clear manual equipment selection
        this.mainStore.clearSelectedMappedEquipments();

        // show message bobble
        this.chatStore.addMessage({
            isMine: true,
            type: MessageType.Text,
            text: getEquipmentLabel(equipment),
            date: new Date(),
        });

        if (!equipment.mapped_equipment_ids || equipment.mapped_equipment_ids.length === 0) {
            return;
        }

        try {
            return this.getMappedEquipmentInfo(equipment.mapped_equipment_ids ?? []);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            if (axios.isAxiosError(error)) {
                // TODO: handle error
                return;
            }
            throw error;
        }
    };

    @action
    submitManualEquipment = async (
        data: ManualEquipmentData
    ): Promise<MappedEquipmentApiModel[] | undefined> => {
        if (!data.modelNumber) {
            return;
        }
        // if we are submitting manual equipment, we need to clear selected installed equipment
        this.mainStore.clearSelectedMonolithInstalledEquipment();

        this.chatStore.addMessage({
            isMine: true,
            type: MessageType.Text,
            text: `${data.manufacturer?.label} - ${data.modelSeries?.label} - ${data.modelNumber?.label}`,
        });

        try {
            return await this.getMappedEquipmentInfo([data.modelNumber.id]);
        } finally {
            /* empty */
        }

        return undefined;
    };

    getMappedEquipmentInfo = async (ids: string[]) => {
        try {
            this.isFetchingInfo = true;
            const response = await this.equipmentsApi.getEquipmentInfo({
                ...getBaseRequestParams(),
                location_id: this.mainStore.selectedAppointment!.locationId,
                mapped_equipment_ids: ids,
            });

            if (response.data.mapped_equipments) {
                this.mainStore.setSelectedMappedEquipments(response.data.mapped_equipments);
                return response.data.mapped_equipments;
            }
        } finally {
            runInAction(() => {
                this.isFetchingInfo = false;
            });
        }
    };
}
